import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getShiftBaseConfigByUfApi,
  upsertShiftBaseConfigApi,
  ShiftBaseConfig,
} from "../../../views/scheduling/shift-base-config/shiftBaseConfigApi";

type ShiftBaseConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  token: string;
  initialUf?: string;
  onSaved?: (cfg: ShiftBaseConfig) => void;
};

const UFS = [
  "RJ","SP",
];

const ShiftBaseConfigModal: React.FC<ShiftBaseConfigModalProps> = ({
  isOpen,
  onClose,
  token,
  initialUf,
  onSaved,
}) => {
  const [uf, setUf] = useState<string>("");
  const [baseValue, setBaseValue] = useState<string>("");
  const [baseHours, setBaseHours] = useState<number>(9);
  const [loading, setLoading] = useState(false);
  const [loadingUf, setLoadingUf] = useState(false);
  const [isEditingExisting, setIsEditingExisting] = useState(false);

  const normalizedUf = (uf ?? "").trim().toUpperCase();

  const canSubmit = useMemo(() => {
    if (loading || loadingUf) return false;
    if (!/^[A-Z]{2}$/.test(normalizedUf)) return false;
    if (!baseValue.trim()) return false;
    return true;
  }, [loading, loadingUf, normalizedUf, baseValue]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    setUf(initialUf ?? "");
    setBaseValue("");
    setBaseHours(9);
    setLoading(false);
    setLoadingUf(false);
    setIsEditingExisting(false);
  }, [isOpen, initialUf]);

  useEffect(() => {
    if (!isOpen) return;
    if (!/^[A-Z]{2}$/.test(normalizedUf)) return;

    let cancelled = false;

    (async () => {
      try {
        setLoadingUf(true);
        const cfg = await getShiftBaseConfigByUfApi(normalizedUf, token);
        if (cancelled) return;

        setIsEditingExisting(true);
        setBaseValue(cfg.baseValue ?? "");
        setBaseHours(cfg.baseHours ?? 9);
      } catch (err: any) {
        // Se não existir, seguimos em modo "criar"
        if (cancelled) return;
        setIsEditingExisting(false);
        setBaseValue("");
        setBaseHours(9);
      } finally {
        if (!cancelled) setLoadingUf(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, normalizedUf, token]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!loading) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setLoading(true);

      const saved = await upsertShiftBaseConfigApi(normalizedUf, token, {
        baseValue: baseValue.trim(),
        baseHours,
      });

      toast.success(isEditingExisting ? "Diária atualizada!" : "Diária cadastrada!");
      onSaved?.(saved);
      onClose();
    } catch (error) {
      toast.error("Erro ao salvar diária.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick} role="presentation">
      <div className="ModalShell" onClick={handleModalClick} role="dialog" aria-modal="true">
        <h2 className="ModalTitle">
          {isEditingExisting ? `Editar diária (${normalizedUf})` : "Cadastrar diária por UF"}
        </h2>

        <form className="ModalBody" onSubmit={handleSubmit}>
          <div className="ModalField">
            <label className="ModalLabel">UF</label>
            <select
              className="ModalInput"
              value={uf}
              onChange={(e) => setUf(e.target.value)}
              disabled={loading || !!initialUf}
            >
              <option value="">Selecione...</option>
              {UFS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>

          <div className="ModalField">
            <label className="ModalLabel">Valor da diária</label>
            <input
              className="ModalInput"
              placeholder='Ex.: 130,00'
              value={baseValue}
              onChange={(e) => setBaseValue(e.target.value)}
              disabled={loading || loadingUf || !/^[A-Z]{2}$/.test(normalizedUf)}
              autoFocus={!initialUf}
            />
          </div>

          <div className="ModalField">
            <label className="ModalLabel">Horas base (opcional)</label>
            <input
              className="ModalInput"
              type="number"
              min={1}
              value={baseHours}
              onChange={(e) => setBaseHours(Number(e.target.value || 9))}
              disabled={loading || loadingUf || !/^[A-Z]{2}$/.test(normalizedUf)}
            />
          </div>

          <div className="ModalActions">
            <button
              type="button"
              className="ModalButton ModalButtonCancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="ModalButton ModalButtonConfirm"
              disabled={!canSubmit}
            >
              {loading ? "Salvando..." : "Confirmar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShiftBaseConfigModal;
